// Area: ai (owner ai-runtime in wave 1; copilot-panel in wave 2).
//
// The caller's AI status: the light part of the AI contracts. `useAiAvailability()` renders in the
// workspace rail on every screen, so this module imports nothing but zod and `defineContract`; the
// rest of the AI contracts (`@dataroom/contracts/ai`, which re-exports this module) pulls in the
// authoring schemas of documents, dashboards and workflows and stays out of the first paint (§12).
import { z } from 'zod';
import { defineContract } from '../../define';

/**
 * Capabilities a workspace routes to a model. `agent` is the copilot and tool loop, `structured`
 * drafts with a schema, `inline` fast rewrites in editors, `vision` image understanding, `images`
 * image generation, `research` web research, `workflow` AI steps in automations.
 */
export const AiCapability = z.enum([
  'agent',
  'structured',
  'inline',
  'vision',
  'images',
  'research',
  'workflow',
]);
export type AiCapability = z.infer<typeof AiCapability>;

export const AiBudgetStatus = z.object({
  scope: z.enum(['workspace', 'member']),
  limitTokens: z.number().int().nullable(),
  usedTokens: z.number().int(),
  remainingTokens: z.number().int().nullable(),
  softReached: z.boolean(),
  hardReached: z.boolean(),
  resetsAt: z.string(),
});
export type AiBudgetStatus = z.infer<typeof AiBudgetStatus>;

/** What a member may use right now (drives `useAiAvailability()` in the client). */
export const AiStatusDTO = z.object({
  available: z.boolean(),
  reason: z.enum(['ready', 'disabled', 'not_configured', 'budget_exhausted']),
  capabilities: z.record(AiCapability, z.boolean()),
  localOnly: z.boolean(),
  canManage: z.boolean(),
  /** Concurrent runs per member. */
  maxConcurrentRuns: z.number().int(),
  budgets: z.array(AiBudgetStatus),
  /** Chat models already routed in this workspace on the active agent provider. No credentials. */
  models: z
    .array(z.object({ model: z.string(), providerName: z.string(), isDefault: z.boolean() }))
    .optional(),
});
export type AiStatusDTO = z.infer<typeof AiStatusDTO>;

export const getAiStatus = defineContract({
  method: 'GET',
  path: '/ai/status',
  response: AiStatusDTO,
  summary: 'Whether AI is available to the caller, per capability, with budgets',
});
